import * as Eta from 'eta'

import { categories, CCM_FIELDS, fields } from '../ccm'
import { CustomerMapParsed, FieldMapping, SupportedTools } from '../interfaces'

import { DatasourceMap } from './render/rtd/datasource.types'
import { AstNode } from './render/types'

Eta.configure({
  tags: ['{{!', '!}}'],
  useWith: true,
})

export interface RenderOptions {
  includeToolPrefix?: boolean // defaults to true
}

/**
 * Single classification field used for both:
 *  - **RTD routing** in `render-ioc.ts`:
 *      `siem` / `ndr` → matrix path (`renderSiemGrouped`): honors CCM_HUNT_CATEGORY_MAP +
 *        per-tool `supportedLogCategories`; emits one query per (huntCat, logCat) pair with
 *        matrix pruning.
 *      `edr` / `xdr` → single-source path (`renderSingleSourceGrouped`): bypasses the matrix;
 *        emits one query per hunt_category the tool's `datasourceMap` declares, tagged with a
 *        fixed log_category label derived from `toolKind`.
 *      `misc` → not RTD-renderable (Snort/Yara are rule formats, not query languages);
 *        these tools are gated out earlier by `supportsRtd()`.
 *
 *  - **User-facing category labels** (Settings → Environment → Tool, custom hunt package
 *    dropdowns): consumers uppercase `toolKind` to derive the display label
 *    (`'siem'` → `'SIEM'`, `'ndr'` → `'NDR'`, etc.).
 *
 * Historical note: `toolKind` and a separate UPPERCASE `category` field on the legacy
 * `SupportedMappings` array were maintained in parallel, which drifted (e.g. Corelight
 * Investigator was `toolKind='siem'` but `category='NDR'`). HUN-1195 collapsed them onto
 * this one field. NDR tools route the same as SIEM (matrix path) — Corelight previously
 * piggybacked on `'siem'` for that reason.
 */
export type ToolKind = 'siem' | 'edr' | 'xdr' | 'ndr' | 'misc'

/** CCM category keys (e.g. `'endpoint:wineventlog'`) — keeps `allowedCategories` typo-safe. */
export type CCMCategoryKey = keyof typeof categories

/**
 * Base definition for all Tool classes
 *
 * Allows overriding only the necessary methods per product as needed, without requiring consuming code
 * to have substantial branching for all minor variants between each platform
 */
export abstract class Tool {
  // Well-formatted name of the tool, used when displaying tool name to user
  public abstract readonly toolName: SupportedTools

  /**
   * Tool's classification. See `ToolKind` docstring for full semantics — this single field
   * drives both RTD pipeline routing and the user-facing category label (uppercased).
   */
  public abstract readonly toolKind: ToolKind

  /**
   * Whether the tool supports user-defined custom field mappings on its environment config.
   * Drives the "matched / missing fields" UI on generated content. Default false; SIEMs with
   * configurable schemas (Splunk, Elastic/Signal, MS Sentinel, QRadar) override to true.
   */
  public readonly customMapping: boolean = false

  /**
   * Tool-specific fields the user must fill in when configuring a field mapping in Settings
   * (e.g. Splunk needs `index` + `sourceType`; MS Sentinel needs `tableName`). Surfaced as
   * required form controls by the tool config UI.
   */
  public readonly toolSpecific?: string[]

  /**
   * If set, restricts the CCM categories selectable for this tool's field mappings to this
   * list (otherwise all categories from `ccm.ts` are offered).
   */
  public readonly allowedCategories?: CCMCategoryKey[]

  // Default CCM values for all fields mapped to themselves, used
  // as lowest priority fallback fields to use when not provided by a customer fieldMapping
  private globalDefaultMappedFields: {
    [ccmField: string]: string
  } = Object.keys(fields).reduce((obj, key) => {
    obj[key] = key
    return obj
  }, {})

  public readonly toolDefaultCCMFieldMappings: { [ccmField: string]: string } =
    {}

  /**
   * Optional per-tool datasource map used to resolve category + platform → event routing metadata.
   * Override in tool subclasses that have datasource mappings.
   */
  public readonly datasourceMap?: DatasourceMap

  /**
   * CCM log categories (ccm_log_categories values from CCM_HUNT_CATEGORY_MAP)
   * that this tool can actually consume. Used to gate IOC RTD fanout so
   * EDR-only tools don't receive endpoint:sysmon groupings, etc.
   * Override per tool; empty/undefined means "no gating" (legacy behavior).
   */
  public supportedLogCategories?(): string[]

  public fieldMappingCustomFields: Array<CCM_FIELDS>

  /**
   *
   * @param contentTemplate Content template string to be rendered using customer mappings
   * @param fieldMapping One set of matched mappings to be injected as template context
   */
  public renderContentTemplate(
    contentTemplate: string,
    pivotFields?: { [fieldName: string]: string },
    fieldMapping?: FieldMapping,
    options?: RenderOptions,
    helpers?: Record<string, (...args: any[]) => any>,
  ): string {
    const finalFields: Record<string, string> = {}

    if (fieldMapping?.fields) {
      for (const entry of fieldMapping.fields) {
        const [key, value] = Object.entries(entry)[0] ?? []
        if (value) {
          finalFields[key] = value
        }
      }
    }

    // Extract pivot fields and escape if supported
    const pivotContext: Record<string, string> = {}
    if (pivotFields) {
      for (const [key, value] of Object.entries(pivotFields)) {
        pivotContext[key] = this.pivotEscape
          ? this.pivotEscape(key, value)
          : value
      }
    }

    const context = {
      ...this.globalDefaultMappedFields,
      ...this.toolDefaultCCMFieldMappings,
      ...finalFields,
      pivot: pivotContext,
      ...(helpers ?? {}), // inject tool-specific helpers here
    }

    return Eta.render(contentTemplate, context) as string
  }

  /**
   * Optional method to escape pivot content before rendering
   *
   * @param str The string to escape
   */
  public pivotEscape?(key: string, value: string): string

  /**
   * Optional method to render an tuning AST into a tool-specific query string
   *
   * @param ast The AST to render
   */
  public renderTuning?: (ast: AstNode) => string

  /**
   * Helper text about where to place tuning query for this tool
   */
  public readonly tuningText?: string

  /**
   * Optional method to support uploading and parsing a custom mapping export from the respective platform
   *
   * @param fileContents File upload contents to parse into structure CustomerMapParsed
   */
  public parseMapUpload?(fileContents: string): CustomerMapParsed

  /**
   *
   * @returns true when parseMapUpload method is defined on a Tool subclass
   */
  public supportsMapUploads = (): boolean => {
    return typeof this.parseMapUpload === 'function'
  }

  /**
   * Normalizes a root URL by stripping trailing slashes.
   * Subclasses may override to additionally strip tool-specific path segments
   *
   * @param url The raw rootUrl as configured by the user
   * @returns Normalized URL with no trailing slash
   */
  public normalizeRootUrl(url: string): string {
    return url.replace(/\/+$/, '')
  }

  /**
   * Optional method to generate a direct-to-tool search permalink based on user's provided tool URLs
   * and the provided content query string
   *
   * @param rootURL Root URL to the tool
   * @param renderedContent The content to be serialized into the generated search URL based on the tool's serialization method
   */
  public generateSearchPermalink?(
    rootURL: string,
    renderedContent: string,
  ): string

  /**
   *
   * @returns true when generateSearchPermalink method is defined on a Tool subclass
   */
  public supportsSearchPermalinks = (): boolean => {
    return typeof this.generateSearchPermalink === 'function'
  }

  /**
   *
   * @returns true when escape method is defined on a Tool subclass
   */
  public supportsPivot = (): boolean => {
    return typeof this.pivotEscape === 'function'
  }

  /**
   * @returns true when renderTuning is defined on a Tool subclass,
   * indicating the tool supports tuning rendering
   */
  public supportsTuning = (): boolean => {
    return typeof this.renderTuning === 'function'
  }

  /**
   * @returns true when the tool supports RTD rendering.
   * Defaults to supportsTuning(); override in subclasses that support
   * tuning but not RTD (e.g. QRadar).
   */
  public supportsRtd = (): boolean => {
    return this.supportsTuning()
  }
}

/**
 * Default Tool class used when no more specific class is available
 */
export class DefaultTool extends Tool {
  // TODO: Gross for workaround on abstract
  public readonly toolName = 'Default Tool' as SupportedTools
  public readonly toolKind: ToolKind = 'siem'
}
